import React from 'react';
import { getPrivacyRequirements, getConsentText } from '../../utils/privacyCompliance';

interface StatePrivacyNoticeProps {
  state?: string;
  age?: number;
  onAccept: () => void;
  onOptOut?: () => void;
}

export const StatePrivacyNotice: React.FC<StatePrivacyNoticeProps> = ({
  state,
  age,
  onAccept,
  onOptOut,
}) => {
  const requirements = getPrivacyRequirements(state);
  const consentText = getConsentText(state, age);

  // Special handling for California users
  const isCalifornia = state === 'CA';
  const isUnder16 = age && age < 16;

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-rowing-gray-900 mb-2">Privacy Rights Notice</h2>
        {state && (
          <p className="text-sm text-rowing-gray-600">
            As a {getStateName(state)} resident, you have additional privacy rights.
          </p>
        )}
      </div>

      <div className="space-y-6">
        {/* Primary consent text */}
        <div className="bg-rowing-gray-50 rounded-lg p-4">
          <p className="text-sm text-rowing-gray-700 font-medium mb-2">{consentText.primary}</p>
          {consentText.additional.length > 0 && (
            <ul className="text-xs text-rowing-gray-600 space-y-1 list-disc list-inside">
              {consentText.additional.map((text, index) => (
                <li key={index}>{text}</li>
              ))}
            </ul>
          )}
        </div>

        {/* State-specific rights */}
        {requirements.additionalNotices.length > 0 && (
          <div className="border border-rowing-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-rowing-gray-900 mb-3">Your Privacy Rights</h3>
            <ul className="space-y-2">
              {requirements.additionalNotices.map((notice, index) => (
                <li key={index} className="flex items-start">
                  <svg
                    className="w-5 h-5 text-rowing-blue mr-2 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm text-rowing-gray-700">{notice}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* California-specific "Do Not Sell" option */}
        {isCalifornia && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-amber-900 mb-2">California Privacy Rights</h3>
            <p className="text-xs text-amber-800 mb-3">
              Under the California Consumer Privacy Act (CCPA), you have the right to opt-out of the
              sale of your personal information.
            </p>
            {isUnder16 ? (
              <p className="text-xs text-amber-800 font-medium">
                Note: We do not sell the personal information of users under 16 without affirmative
                authorization.
              </p>
            ) : (
              <button
                onClick={onOptOut}
                className="text-sm text-amber-900 font-medium hover:underline"
              >
                Do Not Sell My Personal Information →
              </button>
            )}
          </div>
        )}

        {/* Data rights summary */}
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="flex items-center">
            <span className="mr-2">✓</span>
            <span
              className={
                requirements.dataPortabilityRequired ? 'font-medium' : 'text-rowing-gray-500'
              }
            >
              Data Portability {requirements.dataPortabilityRequired ? 'Available' : 'N/A'}
            </span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">✓</span>
            <span
              className={
                requirements.rightToDeleteRequired ? 'font-medium' : 'text-rowing-gray-500'
              }
            >
              Right to Delete {requirements.rightToDeleteRequired ? 'Available' : 'Limited'}
            </span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">✓</span>
            <span className={requirements.optOutRequired ? 'font-medium' : 'text-rowing-gray-500'}>
              Opt-Out Rights {requirements.optOutRequired ? 'Available' : 'N/A'}
            </span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">✓</span>
            <span className="font-medium">Non-Discrimination Guaranteed</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="pt-4">
          <button onClick={onAccept} className="w-full btn-primary py-3">
            I Understand My Privacy Rights
          </button>
          <p className="text-center mt-3 text-xs text-rowing-gray-500">
            By continuing, you acknowledge that you have read and understand your privacy rights.
            You can exercise these rights at any time from your account settings.
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper function to get full state name
function getStateName(stateCode: string): string {
  const stateNames: Record<string, string> = {
    CA: 'California',
    VA: 'Virginia',
    CO: 'Colorado',
    CT: 'Connecticut',
    UT: 'Utah',
  };
  return stateNames[stateCode] || stateCode;
}
